/**
 * Das Schnapskonto — Ledger mit aufklappbaren Begründungsketten und der
 * Tilgung mit Bestätigung durch ein zweites Mitglied.
 */
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import { api, ApiFehler } from '../api/client.js'
import { formatDatumZeit, formatEinheiten, formatSchnaepse } from '../api/format.js'
import { useLaden } from '../api/laden.js'
import { useSitzung } from '../api/sitzung.js'
import type { Konto as KontoDaten, LedgerPosten, Mitglied } from '../api/typen.js'
import {
  Abschnitt,
  Amtsknopf,
  Fehlermeldung,
  Haarlinie,
  Laufvermerk,
  LeererZustand,
  Stempel,
  Urkunde,
} from '../bausteine/Bausteine.js'

export function Konto() {
  const { memberId } = useParams()
  const { mitglied } = useSitzung()
  const eigenes = memberId === undefined
  const pfad = eigenes ? '/api/konto' : `/api/konto/${memberId}`

  const konto = useLaden(() => api.get<KontoDaten>(pfad), [pfad])
  const mitglieder = useLaden(() => api.get<Mitglied[]>('/api/members'))

  if (konto.laedt || mitglieder.laedt) return <Laufvermerk />
  if (konto.fehler) return <Fehlermeldung text={konto.fehler} />
  const daten = konto.daten
  if (!daten) return null

  const inhaber = mitglieder.daten?.find((m) => m.id === daten.memberId)

  return (
    <>
      <section className="pt-10 pb-6 text-center">
        <p className="protokoll">
          Schnapskonto · {inhaber ? `${inhaber.avatarEmoji} ${inhaber.name}` : `Mitglied ${daten.memberId}`}
        </p>
        <p className="urteil mt-3">{formatSchnaepse(daten.offeneUnits, daten.unitCl)}</p>
        <p className="protokoll mt-2 normal-case">offene Schuld</p>
      </section>

      <Haarlinie />

      <Abschnitt titel="Ledger">
        {daten.posten.length === 0 ? (
          <LeererZustand text="Keine offenen Posten. Die Kammer ruht." />
        ) : (
          <div className="flex flex-col gap-2.5">
            {daten.posten.map((posten) => (
              <KontoPosten
                key={posten.id}
                posten={posten}
                mitglieder={mitglieder.daten ?? []}
                darfTilgen={eigenes && posten.memberId === mitglied?.id}
                neuLaden={konto.neuLaden}
              />
            ))}
          </div>
        )}
      </Abschnitt>
    </>
  )
}

function KontoPosten({
  posten,
  mitglieder,
  darfTilgen,
  neuLaden,
}: {
  posten: LedgerPosten
  mitglieder: Mitglied[]
  darfTilgen: boolean
  neuLaden: () => void
}) {
  const { mitglied } = useSitzung()
  const [aufgeklappt, setAufgeklappt] = useState(false)
  const [tilgungOffen, setTilgungOffen] = useState(false)
  const [bestaetiger, setBestaetiger] = useState<number | ''>('')
  const [alkoholfrei, setAlkoholfrei] = useState(false)
  const [fehler, setFehler] = useState<string | null>(null)
  const [laeuft, setLaeuft] = useState(false)

  const andere = mitglieder.filter((m) => m.isActive && m.id !== mitglied?.id)

  async function tilgen() {
    if (bestaetiger === '') return
    setLaeuft(true)
    setFehler(null)
    try {
      await api.post(`/api/penalties/${posten.id}/tilgung`, {
        zeugeMemberId: bestaetiger,
        alkoholfrei,
      })
      neuLaden()
    } catch (f) {
      setFehler(f instanceof ApiFehler ? f.message : 'Die Anmeldung ist fehlgeschlagen.')
      setLaeuft(false)
    }
  }

  const zeuge = posten.offenerTilgungsantrag
    ? mitglieder.find((m) => m.id === posten.offenerTilgungsantrag?.zeugeMemberId)
    : null

  return (
    <Urkunde randnummer={posten.id}>
      <div className="flex items-start justify-between gap-3">
        <p className="text-sm text-lampe">
          {posten.paragraph} · {formatEinheiten(posten.amountUnits)}
        </p>
        <Stempel status={posten.status} />
      </div>

      <button
        type="button"
        onClick={() => setAufgeklappt((a) => !a)}
        aria-expanded={aufgeklappt}
        className="mt-1 w-full text-left text-xs leading-relaxed text-akte/75 hover:text-akte"
      >
        {aufgeklappt || !posten.reasonChain ? (
          posten.reasonText
        ) : (
          <>
            {posten.reasonText.split(' | ')[0]}
            {posten.reasonText.includes(' | ') && (
              <span className="text-messing"> … Kette aufklappen</span>
            )}
          </>
        )}
      </button>

      {aufgeklappt && posten.reasonChain && posten.reasonChain.length > 1 && (
        <ol className="mt-2 flex flex-col gap-1 border-l border-messing/30 pl-3">
          {posten.reasonChain.map((schritt, i) => (
            <li key={i} className="text-xs text-akte/80">
              <span className="font-akte text-messing">{schritt.paragraph}</span> — {schritt.text} →{' '}
              {formatEinheiten(schritt.units)}
            </li>
          ))}
        </ol>
      )}

      <p className="protokoll mt-2 normal-case">
        {formatDatumZeit(posten.createdAt)}
        {posten.isDeferred && ' · gestundet'}
        {posten.settledAlcoholfree && ' · alkoholfrei getilgt'}
      </p>
      {posten.isDeferred && posten.deferredReason && (
        <p className="mt-1 text-xs text-akte/60">{posten.deferredReason}</p>
      )}

      {posten.offenerTilgungsantrag && (
        <p className="mt-3 border border-messing/40 bg-messing/10 px-3 py-2 text-sm text-akte">
          Tilgung angemeldet — {zeuge ? `${zeuge.avatarEmoji} ${zeuge.name}` : 'der Zeuge'} ist in
          den Zeugenstand gerufen. Erst die Aussage macht es amtlich.
        </p>
      )}

      {darfTilgen && posten.status === 'offen' && !posten.isDeferred && !posten.offenerTilgungsantrag && (
        <div className="mt-3">
          {!tilgungOffen ? (
            <Amtsknopf variante="leder" onClick={() => setTilgungOffen(true)}>
              Tilgen
            </Amtsknopf>
          ) : (
            <div className="flex flex-col gap-2 border-t border-eiche pt-3">
              <p className="protokoll">Wer hat es gesehen? Der Zeuge wird vorgeladen.</p>
              <div className="flex flex-wrap gap-1.5">
                {andere.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => setBestaetiger(m.id)}
                    className={`border px-3 py-1.5 text-sm transition-colors ${
                      bestaetiger === m.id
                        ? 'border-messing bg-messing/15 text-lampe'
                        : 'border-eiche text-akte/80 hover:border-messing/50'
                    }`}
                  >
                    {m.avatarEmoji} {m.name}
                  </button>
                ))}
              </div>
              <label className="flex items-center gap-2 text-sm text-akte/80">
                <input
                  type="checkbox"
                  checked={alkoholfrei}
                  onChange={(e) => setAlkoholfrei(e.target.checked)}
                  className="accent-[#c89b3c]"
                />
                Alkoholfrei getilgt
              </label>
              <Amtsknopf onClick={() => void tilgen()} disabled={bestaetiger === '' || laeuft}>
                {laeuft ? 'Wird angemeldet …' : 'Zeugen vorladen'}
              </Amtsknopf>
              <Fehlermeldung text={fehler} />
            </div>
          )}
        </div>
      )}
    </Urkunde>
  )
}
